/**
 * parseVariants — shared helper for AS-050 / AS-171 variant support.
 *
 * Reads the `variants` array from a field_config object (method A JSON schema).
 * Backward-compat: if `variants` is missing or empty, returns a single synthesized
 * "預設" variant so callers always get a consistent array.
 *
 * AS-171: each returned object now carries `variant_key`.
 * - Real variants: `variant_key` comes from the API (UUID string, non-empty).
 * - Synthesized fallback variant: `variant_key` is '' (empty string sentinel).
 *   Callers that need to gate on "real variant" can check `v.variant_key !== ''`.
 *
 * @param {object|null} fieldConfig — the parsed field_config from a template
 * @returns {Array<{variant_key: string, name: string, is_default: boolean, defaults: object, locked_fields: string[]}>}
 */
export function parseVariants(fieldConfig) {
  if (!fieldConfig) {
    return [{ variant_key: '', name: '預設', is_default: true, defaults: {}, locked_fields: [] }];
  }

  const variants = fieldConfig.variants;
  if (!Array.isArray(variants) || variants.length === 0) {
    return [{ variant_key: '', name: '預設', is_default: true, defaults: {}, locked_fields: [] }];
  }

  return variants.map((v) => ({
    variant_key: v.variant_key || '',
    name: v.name || '未命名',
    description: v.description || '',
    is_default: !!v.is_default,
    defaults: v.defaults || {},
    locked_fields: Array.isArray(v.locked_fields) ? v.locked_fields : [],
  }));
}

/**
 * getDefaultVariant — returns the is_default variant, or the first one.
 *
 * @param {ReturnType<typeof parseVariants>} variants
 * @returns variant object
 */
export function getDefaultVariant(variants) {
  return variants.find((v) => v.is_default) || variants[0];
}
